import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Tooltip from '../components/ui/Tooltip';
import styles from './LandingPage.module.css';
import livePulseLogo from '../assets/live-pulse-logo.png';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../store/AuthContext';
import ThankYouModal from '../components/ui/ThankYouModal';
import ContactForm from '../components/ui/ContactForm';
import ContactFormModal from '../components/ui/ContactFormModal';
import {
    MessageCircle, CheckSquare, Cloud, AlignLeft, ListOrdered, Star, Brain,
    Disc3, ArrowUpDown, LayoutGrid, SlidersHorizontal, ArrowRight, Check,
    Menu, X, Sparkles, Globe, Shield, Clock, Zap, Mail, Phone, MapPin, LogOut,
    Loader2, AlertCircle, ChevronDown,
    Image, Sliders, Hash, MousePointerClick, Wallet, Smile, Shuffle,
    Eye, Calendar, Crosshair, Scale, HelpCircle, Timer, Pencil, Lock,
    Gift, Flame, Ticket, Cherry
} from 'lucide-react';
import { API_PATH } from '../config/api';

const WhatsAppIcon = ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const CONTACT = {
    email: 'livepulse@ottosoft.in',
    mobile: '+91 9189304070',
    address: "Manik Chand Talab, behind Sangam Hotel, Anisabad, Patna, Bihar 800002",
};
const WHATSAPP_NUMBER = '919189304070';
// Pre-fills the chat so a visitor only has to hit Send — encoded once here and reused by
// every wa.me link on this page.
const WHATSAPP_MESSAGE = "Hi LivePulse team! I'd like to know more about your live polling & audience engagement platform.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

/* All poll/activity types — matches app ActivityTypeModal */
const FEATURES = [
    /* ── Original 11 ── */
    { icon: <MessageCircle size={24} />, color: 'var(--cat-blue)', title: 'Audience Q&A', desc: 'Let your audience ask questions in real-time. Moderate and upvote on the fly.' },
    { icon: <CheckSquare size={24} />, color: 'var(--cat-violet)', title: 'Multiple Choice', desc: 'Poll your audience with options to vote on. Instant live results.' },
    { icon: <Cloud size={24} />, color: 'var(--cat-cyan)', title: 'Word Cloud', desc: 'Collect words that appear as a live, dynamic cloud from responses.' },
    { icon: <AlignLeft size={24} />, color: 'var(--cat-green)', title: 'Open Text', desc: 'Gather free-form text responses and feedback from your audience.' },
    { icon: <ListOrdered size={24} />, color: 'var(--cat-orange)', title: 'Ranking', desc: 'Let your audience rank items by preference with live aggregation.' },
    { icon: <Star size={24} />, color: 'var(--cat-amber)', title: 'Rating', desc: 'Ask your audience to rate on a scale. Stars or emojis, any range.' },
    { icon: <Brain size={24} />, color: 'var(--cat-rose)', title: 'Quiz', desc: 'Test knowledge with a live scored quiz and leaderboards.' },
    { icon: <Disc3 size={24} />, color: 'var(--cat-pink)', title: 'Spinner Wheel', desc: 'Spin a wheel to randomly pick from options. Fun and fair.' },
    { icon: <ArrowUpDown size={24} />, color: 'var(--cat-teal)', title: 'Correct Order', desc: 'Have participants arrange items in the right sequence.' },
    { icon: <LayoutGrid size={24} />, color: 'var(--cat-violet)', title: 'Categories', desc: 'Group items into their correct categories. Great for learning.' },
    { icon: <SlidersHorizontal size={24} />, color: 'var(--cat-teal)', title: 'Rating Scale', desc: 'Rate on a numeric scale (e.g. 1–10). Clear and measurable.' },
    /* ── 14 New Types ── */
    { icon: <Image size={24} />, color: 'var(--cat-indigo)', title: 'Image Choice', desc: 'Present image options instead of text. Participants vote by tapping a picture.' },
    { icon: <Sliders size={24} />, color: 'var(--cat-teal)', title: 'Slider Poll', desc: 'Drag a slider to pick a value. Great for sentiment, scales, and ranges.' },
    { icon: <Hash size={24} />, color: 'var(--cat-violet)', title: 'Numeric Input', desc: 'Ask participants to type a number. Get average, median, and distribution.' },
    { icon: <MousePointerClick size={24} />, color: 'var(--cat-cyan)', title: 'Click on Image', desc: 'Participants click directly on an image. See a density heatmap of responses.' },
    { icon: <Wallet size={24} />, color: 'var(--cat-amber)', title: 'Budget Allocation', desc: 'Distribute a fixed number of points across options. Prioritise anything.' },
    { icon: <Smile size={24} />, color: 'var(--cat-pink)', title: 'Reaction Poll', desc: 'One-tap emoji reactions. Fast, fun, and expressive for any audience.' },
    { icon: <Shuffle size={24} />, color: 'var(--cat-violet)', title: 'Pair Matching', desc: 'Match left-column items to the right. Perfect for definitions, capitals, dates.' },
    { icon: <Eye size={24} />, color: 'var(--cat-blue)', title: 'Prediction Poll', desc: 'Let the crowd predict an outcome. Reveal the answer later and compare.' },
    { icon: <Calendar size={24} />, color: 'var(--cat-teal)', title: 'Timeline Ordering', desc: 'Drag events into the correct chronological order. Ideal for history or process flows.' },
    { icon: <Crosshair size={24} />, color: 'var(--cat-rose)', title: 'Hotspot Poll', desc: 'Define clickable zones on an image. See which hotspots get the most attention.' },
    { icon: <Scale size={24} />, color: 'var(--cat-blue)', title: 'Debate Poll', desc: 'Pick a side — Agree or Disagree — and optionally add an argument.' },
    { icon: <HelpCircle size={24} />, color: 'var(--cat-orange)', title: 'Guess the Number', desc: 'Everyone guesses a hidden number. The closest answer wins.' },
    { icon: <Timer size={24} />, color: 'var(--cat-rose)', title: 'Speed Quiz', desc: 'Answer correctly and fast. Leaderboard sorts by accuracy then response time.' },
    { icon: <Pencil size={24} />, color: 'var(--cat-violet)', title: 'Drawing Poll', desc: 'Participants sketch their response. Collect a gallery of drawings with optional voting.' },
    { icon: <Ticket size={24} />, color: 'var(--cat-amber)', title: 'Scratch Card', desc: 'Each participant scratches a card to reveal a random prize. One try each.' },
    { icon: <Cherry size={24} />, color: 'var(--cat-orange)', title: 'Slot Machine', desc: 'Spin three reels — everyone lands on a random prize from your list.' },
];

const formatMonths = (months) => (months === 12 ? '1 year' : months === 36 ? '3 years' : `${months} month${months === 1 ? '' : 's'}`);

// Pricing-dependent FAQ copy is generated from live values (buildFaqs below) so it can never
// contradict the cards in the pricing section, which are also driven by the same data.
const buildFaqs = (coinBonus, platinumTier, platinumPerMonth) => {
    const bonusPercent = Math.round((coinBonus.rate - 1) * 100);
    const coinsAtThreshold = Math.floor(coinBonus.threshold * coinBonus.rate);
    return [
        {
            q: 'What is LivePulse?',
            a: 'LivePulse is a live polling and audience engagement platform for events, classrooms, and webinars. Hosts create real-time polls, quizzes, word clouds, and Q&A sessions, and participants respond instantly from any device.',
        },
        {
            q: 'Do participants need to download an app or create an account?',
            a: 'No. Participants join by entering a 6-digit code (or scanning a QR code) in any web browser — no app install and no account required.',
        },
        {
            q: 'How much does LivePulse cost?',
            a: `LivePulse has a free Starter plan with 10 demo coins, all ${FEATURES.length} activity types, and unlimited participants — no credit card required. Paid plans are coin-based: Silver (₹1 per coin, pay as you go), Gold (₹${coinBonus.threshold} coupon = ${coinsAtThreshold} coins with a ${bonusPercent}% bonus), Platinum (from ₹${platinumPerMonth.toLocaleString('en-IN')}/month, billed ₹${platinumTier.priceRupees.toLocaleString('en-IN')} every ${formatMonths(platinumTier.months)}, for unlimited events, polls, and participants), and Diamond (custom enterprise pricing).`,
        },
        {
            q: 'What activity types does LivePulse support?',
            a: `LivePulse supports ${FEATURES.length} activity types in one platform, including Multiple Choice, Word Cloud, Quiz, Audience Q&A, Rating, Ranking, Spinner Wheel, Image Choice, and Drawing.`,
        },
        {
            q: 'Is there a limit on how many participants can join an event?',
            a: 'The Starter, Silver, and Gold plans include unlimited participants, and the Platinum plan includes unlimited events, polls, and participants.',
        },
        {
            q: 'Can I use LivePulse for classrooms and corporate training, not just live events?',
            a: 'Yes. LivePulse is built for events, classrooms, and webinars alike — any presenter can run polls, quizzes, and Q&A with a live audience.',
        },
    ];
};

// A representative sample for the hero's "pick an activity type" showcase card — the "+N
// more" link below it is computed from FEATURES.length so it can't go stale as types are
// added or removed.
const HERO_SHOWCASE_TILES = [
    { icon: <CheckSquare size={16} />, label: 'Multiple Choice', color: 'var(--cat-violet)' },
    { icon: <MessageCircle size={16} />, label: 'Audience Q&A', color: 'var(--cat-blue)' },
    { icon: <Cloud size={16} />, label: 'Word Cloud', color: 'var(--cat-cyan)' },
    { icon: <AlignLeft size={16} />, label: 'Open Text', color: 'var(--cat-green)' },
    { icon: <Brain size={16} />, label: 'Quiz', color: 'var(--cat-rose)' },
    { icon: <Star size={16} />, label: 'Rating', color: 'var(--cat-amber)' },
    { icon: <ListOrdered size={16} />, label: 'Ranking', color: 'var(--cat-orange)' },
    { icon: <Disc3 size={16} />, label: 'Spinner Wheel', color: 'var(--cat-pink)' },
    { icon: <SlidersHorizontal size={16} />, label: 'Rating Scale', color: 'var(--cat-teal)' },
];

const JOIN_PLACEHOLDERS = [
    'Enter 6-digit event code',
    'Try code like 482913',
    'Type your host code here',
];

// Fallbacks only, used before /payments/pricing responds — the real, superadmin-configured
// numbers (edited from the Pricing page) are fetched live so this section never goes stale.
const DEFAULT_COIN_BONUS = { threshold: 100, rate: 1.5 };
const DEFAULT_PLATINUM_TIERS = [{ months: 1, priceRupees: 2000 }];

const LandingPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [code, setCode] = useState('');
    const [joinLoading, setJoinLoading] = useState(false);
    const [joinError, setJoinError] = useState('');
    const [joinPlaceholder, setJoinPlaceholder] = useState('');
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactSuccess, setContactSuccess] = useState(false);
    const [visibleFeatures, setVisibleFeatures] = useState(6);
    const [activeSection, setActiveSection] = useState('home');
    const [showNavJoin, setShowNavJoin] = useState(false);
    const [openFaq, setOpenFaq] = useState(0);
    const [coinBonus, setCoinBonus] = useState(DEFAULT_COIN_BONUS);
    const [platinumTiers, setPlatinumTiers] = useState(DEFAULT_PLATINUM_TIERS);
    const heroJoinRef = useRef(null);

    // Live, superadmin-configured pricing (edited from the Pricing page) — keeps the Gold
    // and Platinum cards below from ever showing stale numbers. Public endpoint, works for
    // logged-out visitors too.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_PATH}/payments/pricing`);
                const data = await res.json().catch(() => ({}));
                if (cancelled || !res.ok) return;
                if (data.coinBonus) setCoinBonus(data.coinBonus);
                if (Array.isArray(data.platinumTiers) && data.platinumTiers.length > 0) setPlatinumTiers(data.platinumTiers);
            } catch {
                // keep fallback defaults — buttons still work, and CreditsCouponPage re-fetches
                // and re-validates the real price server-side before charging anything.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const sectionIds = ['home', 'features', 'how-it-works', 'pricing', 'faq', 'contact'];
        const elements = sectionIds
            .map((id) => document.getElementById(id))
            .filter(Boolean);

        if (!elements.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleEntry = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

                if (visibleEntry) {
                    setActiveSection(visibleEntry.target.id);
                }
            },
            {
                root: null,
                rootMargin: '-18% 0px -40% 0px',
                threshold: [0.15, 0.3, 0.5, 0.75],
            }
        );

        elements.forEach((element) => observer.observe(element));
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Show the compact "join with code" widget in the navbar once the
    // hero's own join box has scrolled out of view, so a code entry is
    // always reachable — that's the #1 reason people land on this page.
    useEffect(() => {
        if (typeof window === 'undefined' || !heroJoinRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => setShowNavJoin(!entry.isIntersecting),
            { rootMargin: '-68px 0px 0px 0px', threshold: 0 }
        );
        observer.observe(heroJoinRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let phraseIndex = 0;
        let charIndex = 0;
        let deleting = false;
        let timerId;

        const tick = () => {
            const current = JOIN_PLACEHOLDERS[phraseIndex];

            if (!deleting) {
                charIndex += 1;
                setJoinPlaceholder(current.slice(0, charIndex));

                if (charIndex === current.length) {
                    deleting = true;
                    timerId = setTimeout(tick, 1400);
                    return;
                }

                timerId = setTimeout(tick, 70);
                return;
            }

            charIndex -= 1;
            setJoinPlaceholder(current.slice(0, charIndex));

            if (charIndex === 0) {
                deleting = false;
                phraseIndex = (phraseIndex + 1) % JOIN_PLACEHOLDERS.length;
                timerId = setTimeout(tick, 260);
                return;
            }

            timerId = setTimeout(tick, 38);
        };

        timerId = setTimeout(tick, 350);
        return () => clearTimeout(timerId);
    }, []);

    const openContactModal = (e) => {
        if (e) e.preventDefault();
        setContactModalOpen(true);
    };

    // Silver/Gold/Platinum all buy something (coins or a subscription) rather than needing
    // a sales conversation — send a logged-in admin straight to Credits & Coupon to pay;
    // send a logged-out visitor to sign up first, since buying requires an account.
    const handlePlanCta = (action) => {
        if (action === 'signup') {
            navigate('/signup');
        } else if (action === 'buy') {
            navigate(isAuthenticated ? '/credits' : '/signup');
        } else {
            openContactModal();
        }
    };

    // Derived Gold-card figures from the live coin bonus config.
    const bonusPercent = Math.round((coinBonus.rate - 1) * 100);
    const coinsAtThreshold = Math.floor(coinBonus.threshold * coinBonus.rate);
    const bonusCoins = coinsAtThreshold - coinBonus.threshold;

    // Derived Platinum-card figures — the headline price is the lowest per-month rate across
    // all superadmin-configured tiers (typically the longest plan, thanks to bulk discounts),
    // shown as a real "from ₹X / month" figure rather than the flat 1-month price.
    const platinumTier = platinumTiers.reduce(
        (cheapest, t) => (!cheapest || t.priceRupees / t.months < cheapest.priceRupees / cheapest.months ? t : cheapest),
        null
    ) || DEFAULT_PLATINUM_TIERS[0];
    const platinumPerMonth = Math.round(platinumTier.priceRupees / platinumTier.months);

    const faqs = buildFaqs(coinBonus, platinumTier, platinumPerMonth);

    const handleSectionLink = (event, sectionId) => {
        if (event) event.preventDefault();
        if (typeof window === 'undefined') return;

        const target = document.getElementById(sectionId);
        if (target) {
            requestAnimationFrame(() => {
                const navbarHeight = 68;
                const top = target.getBoundingClientRect().top + window.scrollY - navbarHeight - 16;
                window.scrollTo({
                    top,
                    behavior: 'smooth',
                });
            });
        }
    };

    const handleContactModalSuccess = () => {
        setContactModalOpen(false);
        setContactSuccess(true);
    };

    const handleJoin = async () => {
        const trimmed = code.trim().toUpperCase();
        if (trimmed.length < 6) return;
        setJoinError('');
        setJoinLoading(true);
        try {
            const res = await fetch(`${API_PATH}/join/${trimmed}`);
            const data = await res.json();
            if (!res.ok) {
                setJoinError(data.error || 'Event not found. Please check the code and try again.');
            } else {
                navigate(`/join/${trimmed}`);
            }
        } catch {
            setJoinError('Could not connect to server. Please try again.');
        } finally {
            setJoinLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* ── Navbar ──────────────────────────────── */}
            <nav className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ''}`}>
                <div className={styles.navInner}>
                    <Link to="/" className={styles.logo}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoBrand} />
                    </Link>

                    <div className={styles.navLinks}>
                        <a
                            href=""
                            className={activeSection === 'home' ? styles.navLinkActive : ''}
                            onClick={(e) => handleSectionLink(e, 'home')}
                        >
                            Home
                        </a>
                        <a
                            href=""
                            className={activeSection === 'features' ? styles.navLinkActive : ''}
                            onClick={(e) => handleSectionLink(e, 'features')}
                        >
                            Features
                        </a>
                        <a
                            href=""
                            className={activeSection === 'how-it-works' ? styles.navLinkActive : ''}
                            onClick={(e) => handleSectionLink(e, 'how-it-works')}
                        >
                            How it works
                        </a>
                        <a
                            href=""
                            className={activeSection === 'pricing' ? styles.navLinkActive : ''}
                            onClick={(e) => handleSectionLink(e, 'pricing')}
                        >
                            Pricing
                        </a>
                        <a
                            href=""
                            className={activeSection === 'faq' ? styles.navLinkActive : ''}
                            onClick={(e) => handleSectionLink(e, 'faq')}
                        >
                            FAQ
                        </a>
                        <a
                            href=""
                            className={activeSection === 'contact' ? styles.navLinkActive : ''}
                            onClick={(e) => handleSectionLink(e, 'contact')}
                        >
                            Contact
                        </a>
                    </div>

                    <div className={styles.navActions}>
                        <div
                            className={`${styles.navJoin} ${showNavJoin ? styles.navJoinVisible : ''} ${joinError ? styles.navJoinError : ''}`}
                            aria-hidden={!showNavJoin}
                        >
                            <input
                                className={styles.navJoinInput}
                                value={code}
                                onChange={(e) => {
                                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                    if (joinError) setJoinError('');
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                                placeholder="Code"
                                inputMode="numeric"
                                aria-label="6-digit event code"
                                title={joinError || undefined}
                                disabled={joinLoading}
                                tabIndex={showNavJoin ? 0 : -1}
                            />
                            <button
                                type="button"
                                className={styles.navJoinBtn}
                                disabled={code.length < 6 || joinLoading}
                                onClick={handleJoin}
                                aria-label="Join event"
                                tabIndex={showNavJoin ? 0 : -1}
                            >
                                {joinLoading
                                    ? <Loader2 size={14} className={styles.joinSpinner} />
                                    : <><span className={styles.navJoinBtnLabel}>Join</span><ArrowRight size={14} /></>
                                }
                            </button>
                        </div>

                        {isAuthenticated ? (
                            <>
                                <Link to="/dashboard" className={styles.navLogin}>Dashboard</Link>
                                <button
                                    type="button"
                                    className={styles.navLogout}
                                    onClick={() => { logout(); setMenuOpen(false); }}
                                    aria-label="Log out"
                                >
                                    <LogOut size={18} />
                                    <span>Logout</span>
                                </button>
                            </>
                        ) : (
                            <Link to="/login" className={styles.navLogin}>Login</Link>
                        )}
                       
                        {!isAuthenticated && (
                            <button
                                type="button"
                                className={styles.navRequestDemo}
                                onClick={() => navigate('/signup')}
                            >
                                Sign Up
                            </button>
                        )} <ThemeToggle />
                        <button
                            type="button"
                            className={styles.hamburger}
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                            aria-expanded={menuOpen}
                        >
                            {menuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                {menuOpen && (
                    <div className={styles.mobileMenu}>
                        <a
                            href=""
                            className={activeSection === 'home' ? styles.navLinkActive : ''}
                            onClick={(e) => { setMenuOpen(false); handleSectionLink(e, 'home'); }}
                        >
                            Home
                        </a>
                        <a
                            href=""
                            className={activeSection === 'features' ? styles.navLinkActive : ''}
                            onClick={(e) => { setMenuOpen(false); handleSectionLink(e, 'features'); }}
                        >
                            Features
                        </a>
                        <a
                            href=""
                            className={activeSection === 'how-it-works' ? styles.navLinkActive : ''}
                            onClick={(e) => { setMenuOpen(false); handleSectionLink(e, 'how-it-works'); }}
                        >
                            How it works
                        </a>
                        <a
                            href=""
                            className={activeSection === 'pricing' ? styles.navLinkActive : ''}
                            onClick={(e) => { setMenuOpen(false); handleSectionLink(e, 'pricing'); }}
                        >
                            Pricing
                        </a>
                        <a
                            href=""
                            className={activeSection === 'faq' ? styles.navLinkActive : ''}
                            onClick={(e) => { setMenuOpen(false); handleSectionLink(e, 'faq'); }}
                        >
                            FAQ
                        </a>
                        <a
                            href=""
                            className={activeSection === 'contact' ? styles.navLinkActive : ''}
                            onClick={(e) => { setMenuOpen(false); handleSectionLink(e, 'contact'); }}
                        >
                            Contact
                        </a>
                        {isAuthenticated ? (
                            <>
                                <Link to="/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</Link>
                                <button type="button" className={styles.mobileLogout} onClick={() => { logout(); setMenuOpen(false); }}>
                                    <LogOut size={18} /> Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
                                <button
                                    type="button"
                                    className={styles.mobileSignup}
                                    onClick={() => { setMenuOpen(false); navigate('/signup'); }}
                                >
                                    Sign Up
                                </button>
                            </>
                        )}
                    </div>
                )}
            </nav>

            {/* ── Hero ────────────────────────────────── */}
            <section className={styles.hero} id="home">
                <div className={styles.heroMesh} />
                <div className={styles.heroGrid} aria-hidden />
                <div className={styles.heroInner}>
                    <div className={styles.heroLeft}>
                        <p className={styles.heroEyebrow}>Professional audience engagement</p>
                        <div className={styles.heroBadge}>
                            <Sparkles size={14} aria-hidden />
                            <span>Real-time polls, Q&A &amp; quizzes — no app required</span>
                        </div>

                        <h1 className={styles.heroTitle}>
                            Engage your audience with <span className={styles.heroTitleGrad}>live</span> interaction
                        </h1>

                        <p className={styles.heroDesc}>
                            One platform for every activity: polls, word clouds, quizzes, Q&A, and more. Share a 6-digit code — participants join from any device. You run the show.
                        </p>

                        {/* Join code input */}
                        <div className={styles.joinWrap} ref={heroJoinRef}>
                            <div className={`${styles.joinBox} ${joinError ? styles.joinBoxError : ''}`}>
                                <input
                                    className={styles.joinInput}
                                    value={code}
                                    onChange={(e) => {
                                        setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                        if (joinError) setJoinError('');
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                                    placeholder={joinPlaceholder || 'Enter 6-digit event code'}
                                    inputMode="numeric"
                                    aria-label="6-digit event code"
                                    disabled={joinLoading}
                                />
                                <button
                                    type="button"
                                    className={styles.joinBtn}
                                    disabled={code.length < 6 || joinLoading}
                                    onClick={handleJoin}
                                    aria-label="Join event"
                                >
                                    {joinLoading
                                        ? <><Loader2 size={15} className={styles.joinSpinner} /> Checking…</>
                                        : <>Join <ArrowRight size={16} /></>
                                    }
                                </button>
                            </div>
                            {joinError
                                ? <p className={styles.joinError} role="alert">
                                    <AlertCircle size={13} />
                                    {joinError}
                                  </p>
                                : <p className={styles.joinHint}>Have a code from your host? Enter all 6 digits, then tap Join.</p>
                            }
                        </div>

                        <div className={styles.heroCta}>
                            <button
                                type="button"
                                className={styles.ctaPrimary}
                                onClick={() => navigate('/signup')}
                            >
                                Start for free <ArrowRight size={16} />
                            </button>
                            <a href="" className={styles.ctaSecondary} onClick={(e) => handleSectionLink(e, 'how-it-works')}>
                                See how it works
                            </a>
                        </div>

                        <div className={styles.heroTrust}>
                            <div className={styles.heroAvatars}>
                                {['var(--cat-violet)', 'var(--cat-blue)', 'var(--cat-teal)', 'var(--cat-amber)'].map((c, i) => (
                                    <div key={i} className={styles.heroAvatar} style={{ background: c, zIndex: 4 - i }} />
                                ))}
                            </div>
                            <span>Trusted by <strong>Industry Leads </strong> </span>
                        </div>
                    </div>

                    {/* Right — activity type showcase card */}
                    <div className={styles.heroRight}>
                        <span className={`sticker-badge ${styles.mockStickerBadge}`}>
                            <LayoutGrid size={11} aria-hidden strokeWidth={3} /> 20+ types
                        </span>
                        <div className={styles.mockCard}>
                            <div className={styles.mockHeader}>
                                <div className={styles.mockLiveLabel}>
                                    <span className={styles.mockLiveDot} />
                                    Live audience pulse
                                </div>
                                <div className={styles.mockAudience}><Sparkles size={12} /> Live now</div>
                            </div>

                            <div className={styles.showcaseTopline}>
                                <span>YOUR EVENT</span>
                                <span>SESSION 04</span>
                            </div>
                            <h3 className={styles.showcaseQuestion}>Your audience is participating.</h3>
                            <p className={styles.showcasePrompt}>See the pulse of every response as it happens.</p>

                            <div className={styles.audienceMetric}>
                                <div className={styles.audienceMetricValue}>124</div>
                                <div className={styles.audienceMetricCopy}>
                                    <strong>participants live</strong>
                                    <span>+18 in the last minute</span>
                                </div>
                                <div className={styles.audienceMetricPulse} aria-hidden />
                            </div>

                            <div className={styles.responseBars}>
                                {HERO_SHOWCASE_TILES.slice(0, 3).map((t, i) => (
                                    <div
                                        key={t.label}
                                        className={styles.responseBarRow}
                                        style={{ '--tile-color': t.color, '--bar-value': `${68 - i * 17}%`, '--tile-delay': `${160 + i * 60}ms` }}
                                    >
                                        <div className={styles.responseBarLabel}><span className={styles.responseBarIcon}>{t.icon}</span><span>{t.label}</span></div>
                                        <div className={styles.responseBarTrack}><span /></div>
                                        <strong>{68 - i * 17}%</strong>
                                    </div>
                                ))}
                            </div>

                            <a href="" className={styles.typeMoreLink} onClick={(e) => handleSectionLink(e, 'features')}>
                                <LayoutGrid size={13} /> Turn any idea into a live activity <ArrowRight size={13} />
                            </a>
                        </div>

                        {/* Floating chips */}
                        <div className={`${styles.chip} ${styles.chipTL}`}>
                            <Zap size={13} aria-hidden /> Real-time sync
                        </div>
                        <div className={`${styles.chip} ${styles.chipBR}`}>
                            <Lock size={13} aria-hidden /> No account needed
                        </div>
                    </div>
                </div>
            </section>

            

            {/* ── Features: All poll types ────────────────────────────── */}
            <section className={styles.features} id="features">
                <div className={styles.featuresBg} aria-hidden />
                <div className={styles.sectionInner}>
                    <div className={styles.featuresHeader}>
                        <p className={styles.sectionLabel}>Activity types</p>
                        <h2 className={styles.sectionTitle}>20+ poll types in one place</h2>
                        <p className={styles.sectionSub}>
                            From Q&A and word clouds to image choice, budget allocation, drawing polls, and speed quizzes — run any activity with a 6-digit code. No app, no sign-up for participants.
                        </p>
                    </div>

                    <div className={styles.featureGrid}>
                        {FEATURES.slice(0, visibleFeatures).map((f, i) => (
                            <article
                                key={i}
                                className={styles.featureCard}
                                style={{ '--feature-color': f.color, '--card-delay': `${(i % 3) * 80}ms` }}
                            >
                                <div className={styles.featureIcon} style={{ color: f.color }}>
                                    {f.icon}
                                </div>
                                <div className={styles.featureText}>
                                    <h3 className={styles.featureTitle}>{f.title}</h3>
                                    <p className={styles.featureDesc}>{f.desc}</p>
                                </div>
                            </article>
                        ))}
                    </div>

                    {visibleFeatures < FEATURES.length && (
                        <div className={styles.loadMoreWrap}>
                            <button
                                type="button"
                                className={styles.loadMoreBtn}
                                onClick={() => setVisibleFeatures(v => Math.min(v + 3, FEATURES.length))}
                            >
                                Load more
                                <span className={styles.loadMoreCount}>
                                    +{Math.min(3, FEATURES.length - visibleFeatures)} of {FEATURES.length - visibleFeatures} remaining
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* ── How it works ───────────────────────── */}
            <section className={styles.howItWorks} id="how-it-works">
                <div className={styles.whyBg} aria-hidden />
                <div className={styles.sectionInner}>
                    <div className={styles.howHeader}>
                        <p className={styles.sectionLabel}>How it works</p>
                        <h2 className={styles.sectionTitle}>Four steps to live engagement</h2>
                        <p className={styles.sectionSub}>From setup to results — no complexity, no downloads for your audience.</p>
                    </div>
                    <div className={styles.stepsWrap}>
                        {[
                            { step: 1, icon: <Zap size={26} />, color: 'var(--cat-amber)', title: 'Create your event', desc: 'Set up an event in the dashboard. Add polls, Q&A, quizzes, or any activity type you need.' },
                            { step: 2, icon: <Globe size={26} />, color: 'var(--cat-blue)', title: 'Share the code', desc: 'Share the 6-digit code or join URL. Participants open it in any browser — no app or sign-up.' },
                            { step: 3, icon: <Shield size={26} />, color: 'var(--cat-green)', title: 'Run & moderate', desc: 'Launch activities when you’re ready. Approve questions, hide responses, control visibility in real time.' },
                            { step: 4, icon: <Clock size={26} />, color: 'var(--cat-violet)', title: 'See results live', desc: 'Watch responses and results as they come in. Export or present — you’re in full control.' },
                        ].map((w, i) => (
                            <React.Fragment key={i}>
                                <article className={styles.stepCard} style={{ '--step-color': w.color }}>
                                    <span className={styles.stepBgNum} aria-hidden>{w.step}</span>
                                    <div className={styles.stepHeader}>
                                        <div className={styles.stepIconWrap} style={{ color: w.color, background: `color-mix(in srgb, ${w.color} 15%, transparent)` }}>
                                            {w.icon}
                                        </div>
                                        <span className={styles.stepNumber} style={{ background: w.color }}>{w.step}</span>
                                    </div>
                                    <h3 className={styles.stepTitle}>{w.title}</h3>
                                    <p className={styles.stepDesc}>{w.desc}</p>
                                </article>
                                {i < 3 && (
                                    <div className={styles.stepConnector} aria-hidden>
                                        <ArrowRight size={18} />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

          

            {/* ── Pricing: Silver, Gold, Platinum, Diamond ────────────────────── */}
            <section className={styles.pricing} id="pricing">
                <div className={styles.pricingBg} aria-hidden />
                <div className={styles.sectionInner}>
                    <div className={styles.pricingHeader}>
                        <p className={styles.sectionLabel}>Pricing</p>
                        <h2 className={styles.sectionTitle}>Simple, coin-based pricing</h2>
                        <p className={styles.sectionSub}>
                            Create a free account and get <strong>10 demo coins</strong> instantly — no credit card needed.
                            Buy coins as you need them, or go unlimited with Platinum.
                        </p>
                    </div>
                    <div className={styles.pricingGrid}>
                        {[
                            {
                                name: 'Starter',
                                tagline: 'Free forever',
                                price: '₹0',
                                period: '',
                                note: '10 demo coins on signup',
                                features: [
                                    '10 coins — free on signup',
                                    `All ${FEATURES.length} poll types`,
                                    'Unlimited participants',
                                    'No credit card needed',
                                ],
                                cta: isAuthenticated ? 'Already signed up' : 'Sign up free',
                                highlighted: false,
                                color: '#10b981',
                                badge: <><Gift size={12} aria-hidden /> Free</>,
                                action: 'signup',
                                disabled: isAuthenticated,
                            },
                            {
                                name: 'Silver',
                                tagline: 'Pay as you go',
                                price: '₹1',
                                period: '= 1 coin',
                                note: 'Coins equal to coupon amount',
                                features: [
                                    'Any coupon amount',
                                    '₹1 coupon = 1 coin',
                                    'Coins never expire',
                                    'Email support',
                                ],
                                cta: 'Buy Silver coins',
                                highlighted: false,
                                color: '#64748b',
                                action: 'buy',
                            },
                            {
                                name: 'Gold',
                                tagline: `${bonusPercent}% bonus coins`,
                                price: `₹${coinBonus.threshold}`,
                                period: `= ${coinsAtThreshold} coins`,
                                note: `+${bonusCoins} bonus on every ₹${coinBonus.threshold} coupon`,
                                features: [
                                    `Min ₹${coinBonus.threshold} coupon`,
                                    `₹${coinBonus.threshold} = ${coinsAtThreshold} coins (+${bonusCoins} bonus)`,
                                    'Coins never expire',
                                    'Phone support',
                                ],
                                cta: 'Buy Gold coins',
                                highlighted: false,
                                color: '#d97706',
                                badge: <><Star size={12} aria-hidden /> {bonusPercent}% Bonus</>,
                                action: 'buy',
                            },
                            {
                                name: 'Platinum',
                                tagline: 'Unlimited access',
                                price: `₹${platinumPerMonth.toLocaleString('en-IN')}`,
                                period: '/ month',
                                note: platinumTier.months === 1
                                    ? 'No coin limits — run everything'
                                    : `Billed ₹${platinumTier.priceRupees.toLocaleString('en-IN')} every ${formatMonths(platinumTier.months)}`,
                                features: [
                                    'Unlimited events',
                                    'Unlimited polls',
                                    'Unlimited participants',
                                    'Priority phone support',
                                ],
                                badge: <><Flame size={12} aria-hidden /> Popular</>,
                                cta: 'Get Platinum',
                                highlighted: true,
                                color: '#8b5cf6',
                                action: 'buy',
                            },
                            {
                                name: 'Diamond',
                                tagline: 'Enterprise',
                                price: 'Custom',
                                period: '',
                                note: 'Tailored for large organisations',
                                features: [
                                    'Custom branding',
                                    'White labeling',
                                    'Unlimited everything',
                                    'Dedicated support',
                                ],
                                cta: 'Contact us',
                                highlighted: false,
                                color: '#0891b2',
                                action: 'contact',
                            },
                        ].map((plan, i) => (
                            <article
                                key={i}
                                className={[styles.planCard, plan.highlighted ? styles.planCardHighlight : ''].filter(Boolean).join(' ')}
                                style={{ '--plan-color': plan.color, '--plan-index': i }}
                            >
                                {plan.badge && <div className={styles.planBadge}>{plan.badge}</div>}
                                <div className={styles.planHead}>
                                    <div className={styles.planName}>{plan.name}</div>
                                    <div className={styles.planTagline}>{plan.tagline}</div>
                                </div>
                                <div className={styles.planPriceLine}>
                                    <span className={styles.planPriceValue}>{plan.price}</span>
                                    {plan.period && <span className={styles.planPeriod}>{plan.period}</span>}
                                </div>
                                {plan.note && <div className={styles.planNote}>{plan.note}</div>}
                                <div className={styles.planDivider} />
                                <ul className={styles.planFeatures}>
                                    {plan.features.map((f, j) => (
                                        <li key={j} className={styles.planFeature}>
                                            <span className={styles.planCheck}>
                                                <Check size={10} color="#fff" />
                                            </span>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    type="button"
                                    className={plan.highlighted ? styles.planCtaPrimary : styles.planCtaSecondary}
                                    onClick={() => handlePlanCta(plan.action)}
                                    disabled={plan.disabled}
                                >
                                    {plan.cta} <ArrowRight size={16} />
                                </button>
                            </article>
                        ))}

                        {/* 6th card — included in all plans */}
                        <article
                            className={`${styles.planCard} ${styles.planCardIncluded}`}
                            style={{ '--plan-color': '#8b5cf6', '--plan-index': 5 }}
                        >
                            <div className={styles.planHead}>
                                <div className={`${styles.planName} ${styles.planNameIcon}`}>
                                    <Sparkles size={17} aria-hidden /> Included in all plans
                                </div>
                                <div className={styles.planTagline}>No matter which plan you choose</div>
                            </div>
                            <div className={styles.planDivider} />
                            <div className={styles.includedGrid}>
                                {[
                                    { icon: <CheckSquare size={14} />, text: `All ${FEATURES.length} poll types` },
                                    { icon: <Sparkles size={14} />, text: 'AI-powered suggestions' },
                                    { icon: <Zap size={14} />, text: 'Real-time results' },
                                    { icon: <LayoutGrid size={14} />, text: 'Export & analytics' },
                                    { icon: <Globe size={14} />, text: 'Unlimited participants' },
                                    { icon: <Shield size={14} />, text: 'Email support' },
                                ].map((f, i) => (
                                    <div key={i} className={styles.includedItem}>
                                        <span className={styles.includedItemIcon}>{f.icon}</span>
                                        {f.text}
                                    </div>
                                ))}
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            {/* ── FAQ ──────────────────────────────────────────────── */}
            <section className={styles.faq} id="faq">
                <div className={styles.sectionInner}>
                    <div className={styles.faqHeader}>
                        <p className={styles.sectionLabel}>FAQ</p>
                        <h2 className={styles.sectionTitle}>Frequently asked questions</h2>
                        <p className={styles.sectionSub}>Everything you need to know before you start your first event.</p>
                    </div>
                    <div className={styles.faqList}>
                        {faqs.map((item, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div key={item.q} className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ''}`}>
                                    <button
                                        type="button"
                                        className={styles.faqQuestion}
                                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                        aria-expanded={isOpen}
                                    >
                                        {item.q}
                                        <ChevronDown size={18} className={styles.faqChevron} aria-hidden />
                                    </button>
                                    <div className={styles.faqAnswer} hidden={!isOpen}>
                                        <p>{item.a}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'FAQPage',
                            mainEntity: faqs.map((item) => ({
                                '@type': 'Question',
                                name: item.q,
                                acceptedAnswer: { '@type': 'Answer', text: item.a },
                            })),
                        }),
                    }}
                />
            </section>

            {/* ── Contact ──────────────────────────────────────────── */}
            <section className={styles.contact} id="contact">
                <div className={styles.contactBg} aria-hidden />
                <div className={styles.sectionInner}>
                    <div className={styles.contactLayout}>

                        {/* Left: info */}
                        <div className={styles.contactLeft}>
                            <p className={styles.sectionLabel}>Contact us</p>
                            <h2 className={styles.contactTitle}>Let's start a<br />conversation</h2>
                            <p className={styles.contactSubtitle}>
                                Have questions about LivePulse? Want to request a demo or need support?
                                We're here — usually respond within a few hours.
                            </p>

                            <div className={styles.contactInfoList}>
                                <a href={`mailto:${CONTACT.email}`} className={styles.contactInfoRow}>
                                    <span className={styles.contactInfoIcon} style={{ '--ci-color': '#8b5cf6' }}>
                                        <Mail size={17} />
                                    </span>
                                    <div>
                                        <div className={styles.contactInfoLabel}>Email us</div>
                                        <div className={styles.contactInfoValue}>{CONTACT.email}</div>
                                    </div>
                                </a>
                                <a href={`tel:${CONTACT.mobile.replace(/\s/g, '')}`} className={styles.contactInfoRow}>
                                    <span className={styles.contactInfoIcon} style={{ '--ci-color': '#10b981' }}>
                                        <Phone size={17} />
                                    </span>
                                    <div>
                                        <div className={styles.contactInfoLabel}>Call us</div>
                                        <div className={styles.contactInfoValue}>{CONTACT.mobile}</div>
                                    </div>
                                </a>
                                <div className={styles.contactInfoRow}>
                                    <span className={styles.contactInfoIcon} style={{ '--ci-color': '#f59e0b' }}>
                                        <MapPin size={17} />
                                    </span>
                                    <div>
                                        <div className={styles.contactInfoLabel}>Our office</div>
                                        <div className={styles.contactInfoValue}>{CONTACT.address}</div>
                                    </div>
                                </div>
                            </div>

                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.contactWhatsApp}
                            >
                                <WhatsAppIcon size={17} />
                                Chat on WhatsApp — fastest reply
                            </a>

                            <div className={styles.contactResponseBadge}>
                                <Zap size={13} />
                                We typically respond within 2–4 hours on business days
                            </div>
                        </div>

                        {/* Right: form */}
                        <div className={styles.contactFormWrap}>
                            <ContactForm
                                onSuccess={() => setContactSuccess(true)}
                                title="Send us a message"
                            />
                        </div>

                    </div>
                </div>
            </section>
            {/* ── Footer ──────────────────────────────── */}
            <footer className={styles.footer}>
                <div className={styles.footerMesh} aria-hidden />
                <div className={styles.footerGrid} aria-hidden />
                <div className={styles.footerInner}>
                    {/* Brand column */}
                    <div className={styles.footerBrand}>
                        <div className={styles.footerLogoRow}>
                            <img src={livePulseLogo} alt="LivePulse" className={styles.footerLogo} />
                        </div>
                        <p className={styles.footerTagline}>Real-time audience engagement.<br />No app. No friction. Just participation.</p>
                        <button type="button" className={styles.footerCtaBtn} onClick={() => navigate('/signup')}>
                            Get started free <ArrowRight size={14} />
                        </button>
                    </div>

                    {/* Links columns */}
                    <div className={styles.footerLinks}>
                        <div className={styles.footerCol}>
                            <div className={styles.footerColTitle}>Product</div>
                            <a href="" onClick={(e) => handleSectionLink(e, 'features')}>Features</a>
                            <a href="" onClick={(e) => handleSectionLink(e, 'how-it-works')}>How it works</a>
                            <a href="" onClick={(e) => handleSectionLink(e, 'pricing')}>Pricing</a>
                            <a href="" onClick={(e) => handleSectionLink(e, 'faq')}>FAQ</a>
                        </div>
                        <div className={styles.footerCol}>
                            <div className={styles.footerColTitle}>Company</div>
                            <Link to="/about">About</Link>
                            <Link to="/terms">Terms of Service</Link>
                            <Link to="/privacy">Privacy Policy</Link>
                        </div>
                        <div className={styles.footerCol}>
                            <div className={styles.footerColTitle}>Contact</div>
                            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
                            <a href={`tel:${CONTACT.mobile.replace(/\s/g, '')}`}>{CONTACT.mobile}</a>
                            <span className={styles.footerAddress}>{CONTACT.address}</span>
                        </div>
                        <div className={styles.footerCol}>
                            <div className={styles.footerColTitle}>Account</div>
                            {isAuthenticated ? (
                                <>
                                    <Link to="/dashboard">Dashboard</Link>
                                    <button type="button" className={styles.footerLogout} onClick={logout}>Logout</button>
                                    <button type="button" className={styles.footerLinkBtn} onClick={openContactModal}>Request demo</button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login">Login</Link>
                                    <Link to="/signup">Sign up</Link>
                                    <button type="button" className={styles.footerLinkBtn} onClick={openContactModal}>Request demo</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.footerBottom}>
                    <span>© {new Date().getFullYear()} LivePulse. All rights reserved.</span>
                    <span className={styles.footerBottomRight}>Design & Developed by <a href="https://ottosoft.in" target="_blank" rel="noopener noreferrer">Ottosoft Private Limited</a></span>
                </div>
            </footer>

            <ContactFormModal
                isOpen={contactModalOpen}
                onClose={() => setContactModalOpen(false)}
                onSuccess={handleContactModalSuccess}
            />
            <ThankYouModal
                isOpen={contactSuccess}
                onClose={() => setContactSuccess(false)}
            />

            {/* Floating WhatsApp & Call — bottom right */}
            <div className={styles.floatingActions}>
                <Tooltip title="Chat on WhatsApp" placement="left">
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.floatingBtn}
                        aria-label="Chat on WhatsApp"
                    >
                        <WhatsAppIcon size={28} />
                    </a>
                </Tooltip>
                <Tooltip title="Call us" placement="left">
                    <a
                        href={`tel:${CONTACT.mobile.replace(/\s/g, '')}`}
                        className={styles.floatingBtn} style={{ color: 'black' }}
                        aria-label="Call us"
                    >
                        <Phone size={24} />
                    </a>
                </Tooltip>
            </div>
        </div>
    );
};

export default LandingPage;
